import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

class StickyAddToCartComponent extends Component {
  // On ne garde que l'essentiel en obligatoire pour éviter que le script plante
  requiredRefs = ['stickyBar', 'addToCartButton'];

  #resetTimeout;
  #isStuck = false;
  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #currentQuantity = 1;
  #scrollHandler = null;

  connectedCallback() {
    super.connectedCallback();
    
    // Initialisation du scroll
    this.#setupScrollEvent();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
    }
    this.#abortController.abort();
  }

  #setupScrollEvent() {
    this.#scrollHandler = () => {
      // DÉCLENCHEMENT : Si on a scrollé plus de 150px, on affiche
      const shouldStick = window.scrollY > 150;

      if (shouldStick !== this.#isStuck) {
        this.#isStuck = shouldStick;
        // On utilise l'attribut standard pour activer le CSS
        this.refs.stickyBar.setAttribute('data-stuck', shouldStick ? 'true' : 'false');
      }
    };

    window.addEventListener('scroll', this.#scrollHandler, { passive: true });
    
    // On lie au vrai bouton d'achat de la page
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }
  }

  // Action de clic sur le bouton collant
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    
    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.dataset.added = 'true';
      
      if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 800);
    }
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;
    
    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    if (variant?.id) this.dataset.currentVariantId = variant.id;
    
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) this.#handleVariantUnavailable();
    this.#updateButtonText();
  };

  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (variantId) this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => option.value)
      .join(' / ');
      
    if (selectedOptions) variantTitleElement.textContent = selectedOptions;
  };

  #handleCartAddComplete = () => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #getProductForm() {
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;
    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(`#shopify-section-${sectionId} product-form-component[data-product-id="${this.dataset.productId}"]`);
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    if (quantityNumber) {
      quantityNumber.textContent = this.#currentQuantity.toString();
    }
    if (quantityDisplay && addToCartButton) {
      quantityDisplay.style.display = (!addToCartButton.disabled && this.#currentQuantity > 1) ? 'inline' : 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}